import { Client } from '@/types'
import { groupsService } from '@/services/groups.service'

/**
 * Truncates a string to the specified maximum length
 */
const truncate = (value: string | undefined | null, maxLength: number): string => {
  if (value === null || value === undefined) return ''
  const strValue = String(value)
  return strValue.length > maxLength ? strValue.substring(0, maxLength) : strValue
}

/**
 * Converts boolean to 0 or 1
 */
const boolToNumber = (value: boolean | undefined): string => {
  if (value === undefined || value === null) return '0'
  return value ? '1' : '0'
}

/**
 * Escapes CSV field (handles semicolons and quotes)
 * Converts any value to string first to handle non-string types
 */
const escapeCsvField = (value: string | number | boolean | undefined | null): string => {
  if (value === null || value === undefined) return ''
  const strValue = String(value)
  if (!strValue) return ''
  if (strValue.includes(';') || strValue.includes('"')) {
    return `"${strValue.replace(/"/g, '""')}"`
  }
  return strValue
}

const getClientGroupId = (client: Client): string => {
  return typeof client.group === 'string' ? client.group : client.group._id
}

/**
 * Exports a single client to CSV row format for "composition du parc"
 */
const exportClientToCompositionDuParcRow = (client: Client, noNcc: string): string => {
  const fields = [
    escapeCsvField(truncate(client.lastname, 50)),
    escapeCsvField(truncate(client.firstname, 50)),
    escapeCsvField(truncate(client.name, 100)),
    escapeCsvField(truncate(client.noSerie, 10)),
    escapeCsvField(noNcc),
    escapeCsvField(truncate(client.noPS, 9)),
    escapeCsvField(truncate(client.address, 100)),
    escapeCsvField(truncate(client.postalCode, 5)),
    escapeCsvField(truncate(client.city, 100)),
    escapeCsvField(client.place || ''),
    boolToNumber(client.autGIE),
    boolToNumber(client.autPublic),
  ]

  return fields.join(';')
}

/**
 * Exports all clients to CSV file "composition du parc.csv"
 */
export const exportAllClientsToCompositionDuParc = async (clients: Client[]): Promise<void> => {
  const uniqueGroupIds = [...new Set(clients.map(getClientGroupId))]
  const equipmentConfigByGroup = new Map<string, string>()

  await Promise.all(
    uniqueGroupIds.map(async groupId => {
      const equipmentConfig = await groupsService.getEquipmentConfig(groupId)
      const noEquipement = equipmentConfig?.noEquipement
      if (noEquipement && String(noEquipement).trim() !== '') {
        equipmentConfigByGroup.set(groupId, String(noEquipement))
      }
    })
  )

  const rows = clients.map(client => {
    const groupId = getClientGroupId(client)
    const noNcc = equipmentConfigByGroup.get(groupId)

    if (!noNcc) {
      throw new Error(
        "Le numéro de NCC (NO_NCC) n'est pas configuré pour le groupe de cette licence. Veuillez configurer l'équipement du groupe dans les paramètres."
      )
    }

    return exportClientToCompositionDuParcRow(client, noNcc)
  })

  const headers = [
    'NOM',
    'PRENOM',
    'RAISON_SOCIALE',
    'NO_SERIE',
    'NO_NCC',
    'NO_PS',
    'ADRESSE',
    'CP',
    'VILLE',
    'LIEU',
    'AUT_GIE',
    'AUT_PUBLIC',
  ]

  const csvContent = [headers.join(';'), ...rows].join('\n')

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'composition du parc.csv'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
